import type {
  ISheetApprovalStatus,
  SheetApprovalType,
} from "@/payroll/payroll.d";

// The gate vocabulary is shared with the workbook's own approval controls —
// both read the same table, so one definition. Re-exported here so this
// feature's imports stay local.
export {
  SHEET_APPROVAL_TYPE,
  type ISheetApprovalStatus,
  type SheetApprovalType,
} from "@/payroll/payroll.d";

/**
 * Why an approver cannot be emailed. Surfaced rather than hidden so a
 * misconfigured assignment reads as a problem, not as a delivery that silently
 * never happened.
 */
export const SHEET_APPROVAL_INELIGIBLE_REASON = {
  missingEmail: "missing_email",
  technicianCannotAccessWorkbook: "technician_cannot_access_workbook",
} as const;

/** Why the whole ask is unavailable. */
export const SHEET_APPROVAL_BLOCKED_REASON = {
  generatedWorkbookNotFound: "generated_workbook_not_found",
  noApprovableSheets: "no_approvable_sheets",
  noApprovers: "no_approvers",
} as const;

export interface ISheetApprovalDelivery {
  id: string;
  request_id: string;
  status: "pending" | "enqueued" | "failed" | "succeeded";
  sent_at: string | null;
  actor_name: string | null;
  recipient_email: string | null;
  error: string | null;
}

export interface ISheetApprovalUnit {
  access_key: string;
  name: string;
  sheet_names: string[];
}

/**
 * A unit with its gate state attached from the workbook's approval payload.
 *
 * The request endpoint deliberately no longer reports whether a unit is signed
 * off — answering that means hashing every cell it covers, which the pay period
 * detail already did for the same units on the same page load. `approvals` is
 * null only for a unit that payload does not cover, which reads as unsigned.
 */
export interface IResolvedSheetApprovalUnit extends ISheetApprovalUnit {
  approvals: Record<SheetApprovalType, ISheetApprovalStatus> | null;
}

export interface ISheetApprovalRecipient {
  user_id: string;
  name: string;
  email: string | null;
  eligible: boolean;
  ineligible_reason: string | null;
  access_keys: string[];
  unit_names: string[];
  sheet_names: string[];
  latest_delivery: ISheetApprovalDelivery | null;
  delivery_history: ISheetApprovalDelivery[];
}

export interface ISheetApprovalRequestState {
  period: { start_date: string; end_date: string; label: string };
  run: { id: string; status: string; created_at: string };
  can_send: boolean;
  cannot_send_reason: string | null;
  approval_units: ISheetApprovalUnit[];
  recipients: ISheetApprovalRecipient[];
  last_requested_at: string | null;
}

export interface ISheetApprovalRequestResponse {
  request: {
    id: string;
    pay_period_id: string;
    ai_script_run_id: string;
    actor_id: string;
    created_at: string;
  };
  deliveries: {
    user_id: string;
    recipient_email: string | null;
    status: "pending" | "enqueued" | "failed" | "succeeded";
    /**
     * True when this recipient was already listed as unreachable in the
     * confirmation the manager approved — an expected omission, not a new
     * failure. False for a delivery that broke while being enqueued.
     */
    skipped: boolean;
    delivery_id: string;
    error: string | null;
  }[];
}
